import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatCard } from "@/components/Primitives";

const TABS = [
  ["overview", "Analytics"],
  ["users", "Users"],
  ["content", "Content"],
  ["scenarios", "Scenarios"],
  ["interviews", "Interviews"],
  ["pricing", "Pricing"],
  ["settings", "Settings"],
  ["audit", "Audit"],
];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newQ, setNewQ] = useState({ category: "linux", difficulty: "medium", question: "", strongSignals: "" });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const loadAll = async () => {
    const results = await Promise.allSettled([
      api.get("/admin/analytics"),
      api.get("/admin/users"),
      api.get("/admin/content/lessons"),
      api.get("/admin/content/scenarios"),
      api.get("/admin/content/interview-questions"),
      api.get("/admin/pricing"),
      api.get("/admin/settings"),
      api.get("/admin/audit-logs"),
    ]);
    const val = (i, path, fallback) =>
      results[i].status === "fulfilled" ? results[i].value.data[path] ?? results[i].value.data : fallback;
    if (results[0].status === "rejected" && results[2].status === "rejected") {
      setError(errMsg(results[0].reason, "Unable to load admin data"));
      return;
    }
    setAnalytics(val(0, "totals") ? results[0].value.data : { totals: {}, content: {}, recentAiUsage: [] });
    setUsers(val(1, "users", []));
    setLessons(val(2, "lessons", []));
    setScenarios(val(3, "scenarios", []));
    setQuestions(val(4, "questions", []));
    setPlans(val(5, "plans", []));
    setSettings(results[6].status === "fulfilled" ? results[6].value.data : { features: {} });
    setLogs(val(7, "logs", []));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2500);
  };

  const saveLesson = async (slug) => {
    const d = drafts[`lesson:${slug}`] || {};
    await api.patch(`/admin/content/lessons/${slug}`, d);
    flash(`Saved lesson ${slug}`);
    loadAll();
  };

  const saveScenario = async (slug) => {
    const d = drafts[`scenario:${slug}`] || {};
    await api.patch(`/admin/content/scenarios/${slug}`, d);
    flash(`Saved scenario ${slug}`);
    loadAll();
  };

  const savePlan = async (slug) => {
    const d = drafts[`plan:${slug}`] || {};
    await api.patch(`/admin/pricing/${slug}`, d);
    flash(`Saved plan ${slug}`);
    loadAll();
  };

  const saveUser = async (id, updates) => {
    await api.patch(`/admin/users/${id}`, updates);
    flash("User updated");
    loadAll();
  };

  const toggleFeature = async (key, value) => {
    const features = { ...settings.features, [key]: value };
    setSettings({ ...settings, features });
    await api.patch("/admin/settings", { features });
    flash(`${key} ${value ? "enabled" : "disabled"}`);
    loadAll();
  };

  const addQuestion = async () => {
    await api.post("/admin/content/interview-questions", {
      ...newQ,
      strongSignals: newQ.strongSignals.split(",").map((s) => s.trim()).filter(Boolean),
      followUps: [],
    });
    setNewQ({ category: "linux", difficulty: "medium", question: "", strongSignals: "" });
    flash("Question added");
    loadAll();
  };

  const deleteQuestion = async (key) => {
    await api.delete(`/admin/content/interview-questions/${key}`);
    flash("Question deleted");
    loadAll();
  };

  const setDraft = (scope, field, value) => setDrafts({ ...drafts, [scope]: { ...(drafts[scope] || {}), [field]: value } });

  if (error) return <p data-testid="admin-error" className="text-sm text-destructive">{error}</p>;
  if (!analytics || !settings) return <div className="grid place-items-center py-24" data-testid="admin-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-8 fade-up" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-primary">admin portal</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">Platform control</h1>
        </div>
        {notice && <p data-testid="admin-notice" className="font-mono text-xs text-[hsl(var(--success))]">{notice}</p>}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto">
          {TABS.map(([v, label]) => (
            <TabsTrigger key={v} value={v} className="rounded-none" data-testid={`admin-tab-${v}`}>{label}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="space-y-8 pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard testId="admin-users-stat" label="Users" value={analytics.totals.users} />
            <StatCard testId="admin-paid-stat" label="Paid subs" value={analytics.totals.paidSubscriptions} sub={`$${analytics.totals.revenueUsd} collected`} />
            <StatCard testId="admin-interviews-stat" label="Interviews" value={analytics.totals.interviews} />
            <StatCard testId="admin-labs-stat" label="Lab submissions" value={analytics.totals.troubleshootingSubmissions} />
            <StatCard testId="admin-ai-stat" label="AI calls" value={analytics.totals.aiCalls} />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight">Content inventory</h2>
            <div className="mt-4 border border-border divide-y divide-border" data-testid="admin-content-counts">
              {Object.entries(analytics.content).map(([k, v]) => (
                <div key={k} className="flex justify-between px-5 py-3 text-sm">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-mono">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight">Recent AI usage</h2>
            <div className="mt-4 border border-border divide-y divide-border" data-testid="admin-ai-usage">
              {analytics.recentAiUsage.length ? analytics.recentAiUsage.map((r, i) => (
                <div key={i} className="flex justify-between gap-4 px-5 py-3 font-mono text-xs">
                  <span>{r.feature}</span>
                  <span className="text-muted-foreground">{r.model} · {r.success ? "ok" : "failed"} · ~{r.approxTokens} tok</span>
                </div>
              )) : <p className="px-5 py-4 text-sm text-muted-foreground">No AI calls yet.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users" className="pt-6">
          <div className="border border-border divide-y divide-border" data-testid="admin-users-list">
            {users.map((u) => (
              <div key={u.id} className="p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`admin-user-${u.email}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{u.name} · {u.email}</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {u.lessonsCompleted} lessons · {u.assessments} assessments · {u.interviews} interviews
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid={`admin-role-${u.email}`}
                    value={u.role}
                    onChange={(e) => saveUser(u.id, { role: e.target.value })}
                    className="bg-background border border-border px-2 py-1.5 text-xs font-mono"
                  >
                    {["user", "content_admin", "platform_admin"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select
                    data-testid={`admin-plan-${u.email}`}
                    value={u.planSlug}
                    onChange={(e) => saveUser(u.id, { planSlug: e.target.value })}
                    className="bg-background border border-border px-2 py-1.5 text-xs font-mono"
                  >
                    {["free", "pro", "career_plus"].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="content" className="pt-6">
          <div className="space-y-px" data-testid="admin-lessons-list">
            {lessons.map((l) => (
              <div key={l.slug} className="border border-border p-5" data-testid={`admin-lesson-${l.slug}`}>
                <p className="font-mono text-xs text-muted-foreground">{l.moduleSlug} · {l.slug}</p>
                <Input
                  data-testid={`admin-lesson-title-${l.slug}`}
                  defaultValue={l.title}
                  onChange={(e) => setDraft(`lesson:${l.slug}`, "title", e.target.value)}
                  className="mt-2 rounded-none"
                />
                <Textarea
                  data-testid={`admin-lesson-summary-${l.slug}`}
                  defaultValue={l.summary}
                  rows={2}
                  onChange={(e) => setDraft(`lesson:${l.slug}`, "summary", e.target.value)}
                  className="mt-2 rounded-none text-sm"
                />
                <Button size="sm" className="mt-3 rounded-none gap-2" onClick={() => saveLesson(l.slug)} data-testid={`admin-lesson-save-${l.slug}`}>
                  <Save className="h-3.5 w-3.5" /> Save
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="scenarios" className="pt-6">
          <div className="space-y-px" data-testid="admin-scenarios-list">
            {scenarios.map((s) => (
              <div key={s.slug} className="border border-border p-5" data-testid={`admin-scenario-${s.slug}`}>
                <p className="font-mono text-xs text-muted-foreground">{s.slug} · {s.severity} · {s.category}</p>
                <Input
                  data-testid={`admin-scenario-title-${s.slug}`}
                  defaultValue={s.title}
                  onChange={(e) => setDraft(`scenario:${s.slug}`, "title", e.target.value)}
                  className="mt-2 rounded-none"
                />
                <Textarea
                  data-testid={`admin-scenario-symptom-${s.slug}`}
                  defaultValue={s.symptom}
                  rows={2}
                  onChange={(e) => setDraft(`scenario:${s.slug}`, "symptom", e.target.value)}
                  className="mt-2 rounded-none text-sm"
                />
                <Button size="sm" className="mt-3 rounded-none gap-2" onClick={() => saveScenario(s.slug)} data-testid={`admin-scenario-save-${s.slug}`}>
                  <Save className="h-3.5 w-3.5" /> Save
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="interviews" className="pt-6 space-y-6">
          <div className="border border-border p-5" data-testid="admin-add-question">
            <p className="overline text-muted-foreground">add interview question</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="admin-q-category" value={newQ.category} onChange={(e) => setNewQ({ ...newQ, category: e.target.value })} placeholder="category" className="rounded-none" />
              <Input data-testid="admin-q-difficulty" value={newQ.difficulty} onChange={(e) => setNewQ({ ...newQ, difficulty: e.target.value })} placeholder="difficulty" className="rounded-none" />
            </div>
            <Textarea data-testid="admin-q-question" value={newQ.question} onChange={(e) => setNewQ({ ...newQ, question: e.target.value })} rows={2} placeholder="Question text" className="mt-3 rounded-none text-sm" />
            <Input data-testid="admin-q-signals" value={newQ.strongSignals} onChange={(e) => setNewQ({ ...newQ, strongSignals: e.target.value })} placeholder="strong signals, comma separated" className="mt-3 rounded-none" />
            <Button size="sm" className="mt-3 rounded-none gap-2" onClick={addQuestion} disabled={newQ.question.trim().length < 10} data-testid="admin-q-add">
              <Plus className="h-3.5 w-3.5" /> Add question
            </Button>
          </div>
          <div className="border border-border divide-y divide-border" data-testid="admin-questions-list">
            {questions.map((q) => (
              <div key={q.key} className="px-4 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm truncate">{q.question}</p>
                  <p className="font-mono text-xs text-muted-foreground">{q.key} · {q.difficulty}</p>
                </div>
                <button onClick={() => deleteQuestion(q.key)} data-testid={`admin-q-delete-${q.key}`} className="text-muted-foreground hover:text-destructive transition-colors duration-150">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-pricing-list">
            {plans.map((p) => (
              <div key={p.slug} className="border border-border p-5" data-testid={`admin-plan-${p.slug}`}>
                <p className="font-display font-bold tracking-tight">{p.name}</p>
                <label className="mt-3 block overline text-muted-foreground">monthly price (usd)</label>
                <Input
                  data-testid={`admin-plan-price-${p.slug}`}
                  type="number"
                  defaultValue={p.priceMonthly}
                  onChange={(e) => setDraft(`plan:${p.slug}`, "priceMonthly", Number(e.target.value))}
                  className="mt-1 rounded-none"
                />
                <label className="mt-3 block overline text-muted-foreground">tagline</label>
                <Input
                  data-testid={`admin-plan-tagline-${p.slug}`}
                  defaultValue={p.tagline}
                  onChange={(e) => setDraft(`plan:${p.slug}`, "tagline", e.target.value)}
                  className="mt-1 rounded-none"
                />
                <Button size="sm" className="mt-4 rounded-none gap-2" onClick={() => savePlan(p.slug)} data-testid={`admin-plan-save-${p.slug}`}>
                  <Save className="h-3.5 w-3.5" /> Save
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="pt-6">
          <div className="border border-border divide-y divide-border" data-testid="admin-settings">
            {Object.entries(settings.features).map(([k, v]) => (
              <div key={k} className="px-5 py-4 flex items-center justify-between">
                <span className="text-sm">{k}</span>
                <Switch checked={v} onCheckedChange={(val) => toggleFeature(k, val)} data-testid={`admin-toggle-${k}`} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="pt-6">
          <div className="border border-border divide-y divide-border" data-testid="admin-audit-logs">
            {logs.length ? logs.map((l, i) => (
              <div key={i} className="px-5 py-3 flex flex-wrap justify-between gap-3 font-mono text-xs">
                <span>{l.action}</span>
                <span className="text-muted-foreground">{l.target}</span>
                <span className="text-muted-foreground">{new Date(l.createdAt).toLocaleString()}</span>
              </div>
            )) : <p className="px-5 py-4 text-sm text-muted-foreground">No audit entries yet.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
